import React from "react";
import { useNavigate } from "react-router-dom";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CustomTable from "../../../Components/Common/Table";
import { Button, Modal, ModalBody } from "@/Components/ui";
import { benefitService } from "@/api/services/benefit.service";
import { BenefitRule } from "@/api/types/benefit";
import { dialogService } from "@/utils/dialogService";

export default function BenefitRuleList() {
  const navigate = useNavigate();
  const [data, setData] = React.useState<BenefitRule[]>([]);
  const [deleteId, setDeleteId] = React.useState<number | null>(null);
  const [modalDelete, setModalDelete] = React.useState(false);

  const fetchData = React.useCallback(async () => {
    try {
      const response = await benefitService.listRules();
      const rows = Array.isArray(response.data) ? response.data : response.data?.results ?? [];
      setData(rows);
    } catch (error: any) {
      await dialogService.handleAlert(error.message, { force: "error" });
    }
  }, []);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openDeleteModal = React.useCallback((id: number) => {
    setDeleteId(id);
    setModalDelete(true);
  }, []);

  const handleDelete = React.useCallback(async () => {
    if (deleteId === null) {
      return;
    }
    try {
      await benefitService.deleteRule(deleteId);
      await dialogService.handleAlert("자동 혜택 규칙이 삭제되었습니다.");
      setModalDelete(false);
      setDeleteId(null);
      fetchData();
    } catch (error: any) {
      await dialogService.handleAlert(error.message, { force: "error" });
    }
  }, [deleteId, fetchData]);

  const columns = React.useMemo(
    () => [
      { Header: "ID", accessor: "id" },
      { Header: "규칙명", accessor: "name" },
      { Header: "타겟", accessor: "target_type" },
      { Header: "시점", accessor: "trigger_type" },
      { Header: "혜택종류", accessor: "reward_kind" },
      { Header: "수량방식", accessor: "issue_mode" },
      { Header: "기본수량", accessor: "issue_count" },
      {
        Header: "상태",
        accessor: "is_active",
        Cell: ({ row }: any) => (row.original.is_active ? "활성" : "비활성"),
      },
      {
        Header: "관리",
        accessor: "actions",
        Cell: ({ row }: any) => {
          const id = row.original.id;
          return (
            <div className="d-flex gap-2">
              <Button onClick={() => navigate(`/benefit-rule-form/${id}`)} color="success" size="sm">수정</Button>
              <Button onClick={() => openDeleteModal(id)} color="danger" size="sm">삭제</Button>
            </div>
          );
        },
      },
    ],
    [navigate, openDeleteModal]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <BreadCrumb title="자동 혜택 규칙" pageTitle="쿠폰/자동화" />
          <div className="flex flex-wrap -mx-3">
            <div className="w-full px-3">
              <div className="card">
                <div className="card-header d-flex align-items-center justify-content-between">
                  <h4 className="card-title mb-0 flex-grow-1">자동 혜택 규칙 목록</h4>
                  <Button onClick={() => navigate(`/benefit-rule-form/0`)} color="success" className="add-btn me-1">
                    <i className="ri-add-line align-bottom me-1"></i> 생성
                  </Button>
                </div>
                <div className="card-body">
                  <CustomTable data={data} columns={columns} onClickSort={undefined} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal isOpen={modalDelete} onClose={() => setModalDelete(false)}>
        <ModalBody className="text-center p-5">
          <div className="mt-4">
            <h4 className="mb-3">삭제 확인</h4>
            <p className="text-muted mb-4">선택한 자동 혜택 규칙을 삭제하시겠습니까?</p>
            <div className="hstack gap-2 justify-content-center">
              <Button onClick={() => setModalDelete(false)} color="light">취소</Button>
              <Button onClick={handleDelete} color="danger">삭제</Button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
}
